import {
  OfferDocument,
  useRltOfferPdfPreviewQuery,
  useRltOfferShowQuery,
  useRltOfferUpdatePostalAddressMutation,
  useRltOfferUpdateRepresentativeMutation,
  useRltOfferUpdateServiceAddressMutation,
} from "@/services/backend/rlt/offers/api/offer-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  DocumentSheet,
  DocumentSheetActions,
  DocumentSheetActionsContent,
  DocumentSheetActionsHeader,
  DocumentSheetContent,
  DocumentSheetContextProvider,
  DocumentSheetHeader,
  DocumentSheetMain,
  DocumentSheetPreview,
  DocumentSheetTitle,
  DocumentSheetTrigger,
  useDocumentSheetContext,
} from "@/shared/bsm/core/document/components/ui/document-sheet";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shared/components/ui/tabs";
import t from "@/lang/lang";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { OfferView } from "@/routes/processes/[processId]/rlt/offers/[offerId]/data/offer-data-route";
import { TreatmentComponent } from "@/services/backend/rlt/treatments/components/treatment-component";
import { Column } from "@/services/backend/rlt/treatments/lib/treatment-component-state";
import { CalculationComponent } from "@/routes/processes/[processId]/rlt/calculations/[calculationId]/calculation/calculation-route";
import { Button } from "@/shared/components/ui/button";
import { Offer } from "@/services/backend/rlt/offers/types/offer";
import {
  DocumentActionsDownload,
  DocumentActionsHeader,
} from "@/shared/bsm/core/document/components/ui/document-immutable-page";
import { ContractDocument } from "@/services/backend/rlt/contracts/api/contract-api";
import {
  DownloadButton,
  DownloadContractButton,
} from "@/routes/processes/[processId]/rlt/offers/[offerId]/_components/offer-download-buttons";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";
import { NavigateToDocumentButton } from "@/services/backend/documents/components/preview/_components/navigate-to-document-button";
import { GSMServiceKind } from "@/services/backend/services/types/kinds";
import { RefreshCw } from "lucide-react";
import {
  DocumentPage,
  DocumentPageContent,
  DocumentPageHeader,
} from "@/shared/bsm/core/document/components/ui/document-page";
import { PostalAddressCardWithMutation } from "@/services/backend/addresses/components/postal-address-card";
import { ServiceAddressCardWithMutation } from "@/services/backend/addresses/components/service-address-card";
import { RepresentativeCardWithMutation } from "@/services/backend/representatives/components/representative-card";

export function RltOfferPreview({ offerId }: { offerId: string }) {
  const {
    data: offer,
    isLoading,
    error,
  } = useRltOfferShowQuery({ id: offerId });

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading) {
    return <RefreshCw className="h-4 w-4" />;
  }

  return (
    <DocumentSheetContextProvider>
      <OfferDocumentSheet offer={offer!} />
    </DocumentSheetContextProvider>
  );
}

function OfferDocumentSheet({ offer }: { offer: Offer }) {
  const { open, setOpen } = useDocumentSheetContext();

  return (
    <DocumentSheet open={open} onOpenChange={setOpen}>
      <DocumentSheetTrigger asChild>
        <Button variant="ghost" size="sm">
          {t("Vorschau")}
        </Button>
      </DocumentSheetTrigger>
      <DocumentSheetContent className="h-[90vh]">
        <DocumentSheetHeader>
          <DocumentSheetTitle>
            {t(offer!.documentKind)} {t("Vorschau")}
          </DocumentSheetTitle>
        </DocumentSheetHeader>
        <DocumentSheetMain>
          {offer!.immutable ? (
            <ImmutableOfferPreview offer={offer!} />
          ) : (
            <MutableOfferPreview offer={offer!} />
          )}
        </DocumentSheetMain>
      </DocumentSheetContent>
    </DocumentSheet>
  );
}

function ImmutableOfferPreview({ offer }: { offer: Offer }) {
  const { open } = useDocumentSheetContext();

  const { data, isLoading, error } = useRltOfferPdfPreviewQuery(
    {
      id: offer.id,
      document: OfferDocument.ALL,
    },
    {
      // This prevents request to the backend if the document sheet has
      // not been opened. Otherwise, whenever the preview component
      // is used, the PDF would be preloaded (and often unnecessarily so).
      skip: !open,
    },
  );

  return (
    <>
      <DocumentSheetPreview
        isLoading={isLoading}
        objectURL={data?.objectURL}
        error={error}
      />
      <ImmutableOfferActions offer={offer} />
    </>
  );
}

function ImmutableOfferActions({ offer }: { offer: Offer }) {
  return (
    <DocumentSheetActions>
      <DocumentActionsHeader
        title={t("Angebot")}
        documentNumber={<DocumentNumberById documentId={offer.id} />}
      />
      <DocumentSheetActionsContent>
        <DocumentActionsDownload>
          <DownloadButton
            offerId={offer.id}
            document={OfferDocument.ALL}
            text={t("Alle Dokumente")}
          />
          <DownloadButton
            offerId={offer.id}
            document={OfferDocument.OFFER}
            text={t("Angebot")}
          />
          <DownloadButton
            offerId={offer.id}
            document={OfferDocument.TREATMENT}
            text={t("Leistungsverzeichnis")}
          />
          <DownloadButton
            offerId={offer.id}
            document={OfferDocument.ORDER}
            text={t("Auftrag")}
          />
          <DownloadContractButton
            contractId={offer.contractId}
            document={ContractDocument.CONTRACT}
            text={t("Vertrag")}
          />
          <DownloadContractButton
            contractId={offer.contractId}
            document={ContractDocument.TREATMENT}
            text={t("Vertrag LV")}
          />
        </DocumentActionsDownload>
      </DocumentSheetActionsContent>
    </DocumentSheetActions>
  );
}

function MutableOfferPreview({ offer }: { offer: Offer }) {
  return (
    <>
      <MutableOfferView offer={offer} />
      <MutableOfferActions offer={offer} />
    </>
  );
}

function MutableOfferView({ offer }: { offer: Offer }) {
  return (
    <div className="md:col-span-4">
      <PersistenceContextProvider>
        <DocumentPage>
          <Tabs defaultValue="data">
            <DocumentPageHeader>
              <TabsList>
                <TabsTrigger value="data">{t("Angebot")}</TabsTrigger>
                <TabsTrigger value="treatment">{t("Leistungen")}</TabsTrigger>
                <TabsTrigger value="calculation">
                  {t("Kalkulation")}
                </TabsTrigger>
                <TabsTrigger value="customer-address">
                  {t("Anschrift")}
                </TabsTrigger>
                <TabsTrigger value="representative">
                  {t("Betreuer")}
                </TabsTrigger>
              </TabsList>
              <PersistenceStateUsingContext className="mr-2 ml-auto" />
            </DocumentPageHeader>
            <DocumentPageContent className="max-h-[75vh] overflow-auto">
              <TabsContent value="data">
                <OfferView offer={offer} />
              </TabsContent>
              <TabsContent value="treatment">
                <TreatmentComponent
                  treatmentId={offer.treatmentId}
                  availableColumns={[
                    Column.Position,
                    Column.ComponentKind,
                    Column.ComponentData,
                    Column.Cycle,
                    Column.Formula,
                    Column.CleanTime,
                    Column.BasePrice,
                    Column.CycleDiscount,
                    Column.SpecialDiscount,
                    Column.FinalPrice,
                    Column.Comment,
                    Column.Actions,
                  ]}
                  defaultColumns={[
                    Column.Position,
                    Column.ComponentKind,
                    Column.BasePrice,
                    Column.CycleDiscount,
                    Column.FinalPrice,
                    Column.Comment,
                    Column.Actions,
                  ]}
                />
              </TabsContent>
              <TabsContent value="calculation">
                <CalculationComponent calculationId={offer.calculationId} />
              </TabsContent>
              <TabsContent value="customer-address">
                <div className="space-y-2">
                  <PostalAddressCardWithMutation
                    id={offer!.id}
                    address={offer!.postalAddress}
                    mutation={useRltOfferUpdatePostalAddressMutation}
                  />
                  <ServiceAddressCardWithMutation
                    id={offer!.id}
                    address={offer!.serviceAddress}
                    mutation={useRltOfferUpdateServiceAddressMutation}
                  />
                </div>
              </TabsContent>
              <TabsContent value="representative">
                <RepresentativeCardWithMutation
                  id={offer!.id}
                  representative={offer!.representative}
                  mutation={useRltOfferUpdateRepresentativeMutation}
                />
              </TabsContent>
            </DocumentPageContent>
          </Tabs>
        </DocumentPage>
      </PersistenceContextProvider>
    </div>
  );
}

function MutableOfferActions({ offer }: { offer: Offer }) {
  return (
    <DocumentSheetActions className="md:col-span-1">
      <DocumentSheetActionsHeader
        title={t(offer.documentKind)}
        documentNumber={<DocumentNumberById documentId={offer.id} />}
      />
      <DocumentSheetActionsContent>
        <NavigateToDocumentButton
          documentId={offer.id}
          documentKind={offer.documentKind}
          processId={offer.processId}
          serviceKind={GSMServiceKind.RLT}
        />
      </DocumentSheetActionsContent>
    </DocumentSheetActions>
  );
}
